// src/components/UserMeet/User-Meet-Private/AdmissionRequestModal.jsx
import React, { useState, useEffect, useCallback } from "react";
import { UserCheck, UserX, Clock, Users } from "lucide-react";

/* ─── Single Request Card ─── */
function RequestCard({ request, onAdmit, onDeny, isProcessing }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatElapsed = (s) => {
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ${s % 60}s ago`;
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-white/[0.03] border border-white/[0.08] rounded-xl hover:border-white/[0.12] transition-all">
      {/* Avatar */}
      <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#C6A87C]/20 to-[#8A6F4A]/20 border border-[#C6A87C]/20 flex items-center justify-center">
        {request.userImage ? (
          <img
            src={request.userImage}
            alt={request.userName}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-base font-bold text-[#C6A87C]">
            {(request.userName || "?")[0].toUpperCase()}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-semibold text-white truncate">
          {request.userName || "Unknown"}
        </p>
        {request.userEmail && (
          <p className="text-[11px] text-white/40 truncate">
            {request.userEmail}
          </p>
        )}
        <div className="flex items-center gap-1 mt-0.5">
          <Clock size={9} className="text-white/25" />
          <span className="text-[10px] text-white/25">
            {formatElapsed(elapsed)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Deny */}
        <button
          onClick={() => onDeny(request)}
          disabled={isProcessing}
          type="button"
          title="Deny"
          className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/20 hover:border-red-500/35 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <div className="w-3 h-3 border border-red-400/30 border-t-red-400 rounded-full animate-spin" />
          ) : (
            <UserX size={14} />
          )}
        </button>

        {/* Admit */}
        <button
          onClick={() => onAdmit(request)}
          disabled={isProcessing}
          type="button"
          title="Admit"
          className="w-8 h-8 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 flex items-center justify-center hover:bg-green-500/20 hover:border-green-500/35 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <div className="w-3 h-3 border border-green-400/30 border-t-green-400 rounded-full animate-spin" />
          ) : (
            <UserCheck size={14} />
          )}
        </button>
      </div>
    </div>
  );
}

/* ─── Main Modal ─── */
export default function AdmissionRequestModal({
  requests,
  onAdmit,
  onDeny,
  onAdmitAll,
  onDenyAll,
  processingIds,
}) {
  if (!requests || requests.length === 0) return null;

  const hasMultiple = requests.length > 1;

  return (
    <>
      {/* ── Floating panel — fixed bottom-right, above the control bar ── */}
      <div className="fixed bottom-28 right-5 z-[9991] w-[340px] animate-slideUp">
        <div
          className="rounded-2xl border border-white/[0.1] overflow-hidden shadow-2xl"
          style={{
            background:
              "linear-gradient(135deg, rgba(10,29,26,0.98) 0%, rgba(6,20,18,0.98) 100%)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
          }}
        >
          {/* Header */}
          <div className="px-4 py-3.5 border-b border-white/[0.08]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                {/* Icon with badge */}
                <div className="relative">
                  <div className="w-8 h-8 rounded-lg bg-[#C6A87C]/15 border border-[#C6A87C]/25 flex items-center justify-center">
                    <Users size={14} className="text-[#C6A87C]" />
                  </div>
                  <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#C6A87C] text-[#061412] text-[9px] font-bold flex items-center justify-center leading-none">
                    {requests.length}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-white leading-tight">
                    {hasMultiple
                      ? `${requests.length} waiting to join`
                      : "Someone wants to join"}
                  </p>
                  <p className="text-[10px] text-white/35 leading-tight mt-0.5">
                    Waiting room
                  </p>
                </div>
              </div>

              {/* Bulk actions */}
              {hasMultiple && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={onDenyAll}
                    type="button"
                    className="text-[10px] px-2 py-1 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all font-semibold"
                  >
                    Deny All
                  </button>
                  <button
                    onClick={onAdmitAll}
                    type="button"
                    className="text-[10px] px-2 py-1 rounded-md bg-green-500/10 border border-green-500/20 text-green-400 hover:bg-green-500/20 transition-all font-semibold"
                  >
                    Admit All
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Request list */}
          <div
            className="p-3 space-y-2 overflow-y-auto"
            style={{ maxHeight: "240px" }}
          >
            {requests.map((request) => {
              const isProcessing = processingIds?.has(request.participantId);

              return (
                <RequestCard
                  key={request.participantId}
                  request={request}
                  onAdmit={onAdmit}
                  onDeny={onDeny}
                  isProcessing={isProcessing}
                />
              );
            })}
          </div>

          {/* Single request — large Admit / Deny buttons */}
          {!hasMultiple && (
            <div className="px-3 pb-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => onDeny(requests[0])}
                disabled={processingIds?.has(requests[0]?.participantId)}
                type="button"
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/20 hover:border-red-500/35 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingIds?.has(requests[0]?.participantId) ? (
                  <div className="w-3 h-3 border border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                ) : (
                  <UserX size={14} />
                )}
                Deny
              </button>
              <button
                onClick={() => onAdmit(requests[0])}
                disabled={processingIds?.has(requests[0]?.participantId)}
                type="button"
                className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-semibold hover:bg-green-500/20 hover:border-green-500/35 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingIds?.has(requests[0]?.participantId) ? (
                  <div className="w-3 h-3 border border-green-400/30 border-t-green-400 rounded-full animate-spin" />
                ) : (
                  <UserCheck size={14} />
                )}
                Admit
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-white/[0.06] bg-white/[0.02]">
            <p className="text-[10px] text-white/25 text-center">
              Waiting participants cannot hear or see the meeting
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
